import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Grid,
    MenuItem
} from '@mui/material';

export interface ErrorLog {
    _id: string;
    timestamp: string;
    level: 'error' | 'warning' | 'info' | 'apitrace';
    message: string;
    stack?: string;
    path: string;
    method: string;
    user?: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
    };
    ip?: string;
    patchComment?: string;
    patchDate?: string;
    patchAnnouncement?: string;
    patchPlatform?: string;
}

interface PatchInfoModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: Partial<ErrorLog>) => void;
    initialData?: ErrorLog;
}

const PatchInfoModal: React.FC<PatchInfoModalProps> = ({ open, onClose, onSave, initialData }) => {
    const [patchComment, setPatchComment] = useState('');
    const [patchDate, setPatchDate] = useState('');
    const [patchAnnouncement, setPatchAnnouncement] = useState('');
    const [patchPlatform, setPatchPlatform] = useState('');

    useEffect(() => {
        if (initialData) {
            setPatchComment(initialData.patchComment || '');
            // Format date for datetime-local input (YYYY-MM-DDThh:mm)
            if (initialData.patchDate) {
                const date = new Date(initialData.patchDate);
                const isoString = date.toISOString().slice(0, 16);
                setPatchDate(isoString);
            } else {
                setPatchDate('');
            }
            setPatchAnnouncement(initialData.patchAnnouncement || '');
            setPatchPlatform(initialData.patchPlatform || '');
        } else {
            setPatchComment('');
            setPatchDate('');
            setPatchAnnouncement('');
            setPatchPlatform('');
        }
    }, [initialData, open]);

    const handleSave = () => {
        onSave({
            patchComment,
            patchDate: patchDate ? new Date(patchDate).toISOString() : undefined,
            patchAnnouncement,
            patchPlatform
        });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Informations de Patch</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Commentaire de patch"
                            multiline
                            rows={3}
                            value={patchComment}
                            onChange={(e) => setPatchComment(e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            type="datetime-local"
                            label="Date et Heure de patch"
                            value={patchDate}
                            onChange={(e) => setPatchDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Annonce du patch"
                            value={patchAnnouncement}
                            onChange={(e) => setPatchAnnouncement(e.target.value)}
                            helperText="Ex: 'Hotfix déployé' ou 'Sera corrigé dans v2.1'"
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            select
                            fullWidth
                            label="Plateforme d'annoncement"
                            value={patchPlatform}
                            onChange={(e) => setPatchPlatform(e.target.value)}
                        >
                            <MenuItem value="Discord">Discord</MenuItem>
                            <MenuItem value="Email">Email</MenuItem>
                            <MenuItem value="Ticket Support">Ticket Support</MenuItem>
                            <MenuItem value="Autre">Autre</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Annuler</Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PatchInfoModal;
